import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type Table as TanStackTable,
  useReactTable,
} from "@tanstack/react-table";
import { format, isValid, parseISO } from "date-fns";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { Check, TriangleAlert, X } from "lucide-react";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { getColumnSortAria, SortableColumnHeader } from "@/components/ui/sortable-column-header";
import { DataTablePagination } from "@/components/ui/table-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { extractRoleName } from "@/utils";

export interface PlatformAccessRecord {
  added_by?: string | null;
  country?: string | null;
  date_joined?: string | null;
  email?: string | null;
  first_name?: string | null;
  id: number;
  last_name?: string | null;
  user_profile?: string | null;
  [key: string]: unknown;
}

export interface RejectModalOptions {
  id: number | undefined;
  isOpen: boolean;
}

interface ListProps {
  errorMessage?: string;
  isFetching?: boolean;
  onSortingChange?: OnChangeFn<SortingState>;
  pageSize?: number;
  records?: PlatformAccessRecord[];
  setRejectModalOptions: (options: RejectModalOptions) => void;
  sorting?: SortingState;
  submitUser: (userId: number) => void;
}

interface PlatformAccessColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type PlatformAccessColumnDef = ColumnDef<PlatformAccessRecord, unknown> & {
  meta?: PlatformAccessColumnMeta;
};

const DEFAULT_PAGE_SIZE = 10;
const EMPTY_VALUE = "-";

export const DEFAULT_SORTING: SortingState = [{ id: "requestedOn", desc: true }];

export const PLATFORM_ACCESS_SORT_OPTIONS = [
  { id: "requestedOn", translationKey: "platformAccess.requestedOn" },
  { id: "accessRequestedBy", translationKey: "platformAccess.accessRequestedBy" },
  { id: "country", translationKey: "platformAccess.country" },
] as const;

const getColumnMeta = (columnDef: ColumnDef<PlatformAccessRecord, unknown>) =>
  columnDef.meta as PlatformAccessColumnMeta | undefined;

const getResolvedPageSize = (pageSize?: number) =>
  pageSize && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;

const formatValue = (value: unknown) => {
  if (value === undefined || value === null) {
    return EMPTY_VALUE;
  }

  const formattedValue = String(value).trim();
  return formattedValue || EMPTY_VALUE;
};

const getDateTimestamp = (date?: string | null) => {
  const timestamp = date ? new Date(date).getTime() : Number.NEGATIVE_INFINITY;
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const formatRequestedOn = (date?: string | null) => {
  if (!date) {
    return EMPTY_VALUE;
  }

  const parsed = parseISO(date);

  return isValid(parsed) ? format(parsed, "d MMM yyyy, HH:mm") : EMPTY_VALUE;
};

const getRecordRowId = (record: PlatformAccessRecord, index: number) =>
  `${record.id}-${record.user_profile ?? "profile"}-${record.country ?? "country"}-${index}`;

const createColumns = ({
  onReject,
  onSubmit,
  t,
}: {
  onReject: (record: PlatformAccessRecord) => void;
  onSubmit: (userId: number) => void;
  t: TFunction;
}): PlatformAccessColumnDef[] => [
  {
    id: "actions",
    header: () => (
      <span className="sr-only">
        {t("common.approve")} / {t("common.reject")}
      </span>
    ),
    cell: ({ row }) => (
      <div className="flex justify-start gap-2">
        <WFPButton
          type="button"
          variant="success-secondary"
          size="sm"
          onClick={() => onSubmit(row.original.id)}
        >
          <Check className="size-4" aria-hidden="true" />
          {t("common.approve")}
        </WFPButton>
        <WFPButton
          type="button"
          variant="destructive-secondary"
          size="sm"
          onClick={() => onReject(row.original)}
        >
          <X className="size-4" aria-hidden="true" />
          {t("common.reject")}
        </WFPButton>
      </div>
    ),
    enableSorting: false,
    meta: {
      cellClassName: "whitespace-nowrap px-3 align-top",
      headerClassName: "px-3",
    },
  },
  {
    id: "requestedOn",
    accessorFn: row => getDateTimestamp(row.date_joined),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("platformAccess.requestedOn")} />
    ),
    cell: ({ row }) => formatRequestedOn(row.original.date_joined),
    sortingFn: "basic",
    meta: {
      cellClassName: "whitespace-nowrap px-3 align-top",
      headerClassName: "px-3",
    },
  },
  {
    id: "accessRequestedBy",
    accessorFn: row => row.added_by ?? undefined,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("platformAccess.accessRequestedBy")} />
    ),
    cell: ({ row }) => formatValue(row.original.added_by),
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-32 max-w-48 whitespace-normal px-3 align-top leading-5",
      headerClassName: "min-w-32 max-w-48 px-3",
    },
  },
  {
    id: "country",
    accessorFn: row => row.country ?? undefined,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("platformAccess.country")} />
    ),
    cell: ({ row }) => formatValue(row.original.country),
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-24 px-3 align-top",
      headerClassName: "min-w-24 px-3",
    },
  },
  {
    id: "role",
    accessorFn: row => extractRoleName(row.user_profile),
    header: () => t("column.role"),
    cell: ({ row }) => formatValue(extractRoleName(row.original.user_profile)),
    enableSorting: false,
    meta: {
      cellClassName: "min-w-28 px-3 align-top capitalize",
      headerClassName: "min-w-28 px-3",
    },
  },
  {
    id: "firstName",
    accessorFn: row => row.first_name,
    header: () => t("fieldName.firstName"),
    cell: ({ row }) => (
      <span className="font-medium text-foreground">{formatValue(row.original.first_name)}</span>
    ),
    enableSorting: false,
    meta: {
      cellClassName: "min-w-28 px-3 align-top",
      headerClassName: "min-w-28 px-3",
    },
  },
  {
    id: "lastName",
    accessorFn: row => row.last_name,
    header: () => t("fieldName.lastName"),
    cell: ({ row }) => formatValue(row.original.last_name),
    enableSorting: false,
    meta: {
      cellClassName: "min-w-28 px-3 align-top",
      headerClassName: "min-w-28 px-3",
    },
  },
  {
    id: "email",
    accessorFn: row => row.email,
    header: () => t("platformAccess.email"),
    cell: ({ row }) => formatValue(row.original.email),
    enableSorting: false,
    meta: {
      cellClassName: "min-w-40 max-w-64 whitespace-normal break-words px-3 align-top leading-5",
      headerClassName: "min-w-40 max-w-64 px-3",
    },
  },
];

const PlatformAccessDataTable = ({
  emptyMessage,
  isLoading,
  table,
}: {
  emptyMessage: string;
  isLoading: boolean;
  table: TanStackTable<PlatformAccessRecord>;
}) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                aria-sort={getColumnSortAria(header.column)}
                className={getColumnMeta(header.column.columnDef)?.headerClassName}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columnCount}>
              <Loading isVisible small />
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length
          ? rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
  );
};

export const List = ({
  errorMessage = "",
  isFetching = false,
  onSortingChange: onSortingChangeProp,
  pageSize = DEFAULT_PAGE_SIZE,
  records = [],
  setRejectModalOptions,
  sorting,
  submitUser,
}: ListProps) => {
  const { t } = useTranslation();
  const resolvedPageSize = getResolvedPageSize(pageSize);
  const hasRecords = records.length > 0;
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: resolvedPageSize,
  });
  const [internalSorting, setInternalSorting] = useState<SortingState>(DEFAULT_SORTING);
  const resolvedSorting = sorting ?? internalSorting;
  const columns = useMemo(
    () =>
      createColumns({
        onReject: record => setRejectModalOptions({ id: record.id, isOpen: true }),
        onSubmit: submitUser,
        t,
      }),
    [setRejectModalOptions, submitUser, t]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageSize === resolvedPageSize
        ? currentPagination
        : { pageIndex: 0, pageSize: resolvedPageSize }
    );
  }, [resolvedPageSize]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(1, Math.ceil(records.length / currentPagination.pageSize));
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [records.length]);

  const onSortingChange: OnChangeFn<SortingState> = updater => {
    (onSortingChangeProp ?? setInternalSorting)(updater);
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  };

  const table = useReactTable({
    columns,
    data: records,
    autoResetPageIndex: false,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: getRecordRowId,
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    state: {
      pagination,
      sorting: resolvedSorting,
    },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert aria-hidden="true" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <PlatformAccessDataTable
        emptyMessage={t("platformAccess.empty")}
        isLoading={isFetching}
        table={table}
      />
      {hasRecords ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
